import { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import {
	DragAndDropId,
	RankWithSecurities,
	SecurityWithRank,
	UpsertSecurityRankParams,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes.ts';
import { Dispatch, SetStateAction } from 'react';
import { makeSecurityRankId } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingUtils.ts';
import { SecurityFeatureType } from '@dealstack/shared/lib/generated';

interface HandleSecurityDragEndProps {
	event: DragEndEvent;
	setDragActiveId: Dispatch<SetStateAction<UniqueIdentifier | null>>;
	setDragOverId: Dispatch<SetStateAction<UniqueIdentifier | null>>;
	localRanks: RankWithSecurities[];
	setLocalRanks: Dispatch<SetStateAction<RankWithSecurities[]>>;
	setUnrankedSecurities: Dispatch<SetStateAction<SecurityWithRank[]>>;
	serverPersonSecurities: SecurityWithRank[];
	selectedEntityId: string;
	upsertSecurityRank: (params: UpsertSecurityRankParams) => void;
	deleteSecurityRank: (security: SecurityWithRank) => void;
}

/**
 * Handles drag and drop end events for security ranking.
 * Performs optimistic UI updates and triggers server-side mutations.
 *
 * WARRANT CARD BEHAVIOR:
 * When dragging a main security card, any warrant cards (cards with warrantOverId matching
 * the dragged card's securityId) automatically move with it as a group. However, only the
 * main security sends a server request - warrant cards are "shadow" cards that follow their
 * main card without needing separate rank mutations. The server's backend logic ensures
 * warrant cards are positioned correctly after their main card when data is fetched.
 *
 * Three possible drop zones:
 * - Sidebar: Unrank a security
 * - Ranking content: Create a new rank with the security
 * - Specific rank container: Move security to that rank
 */
export const handleSecurityDragEnd = ({
	event,
	setDragActiveId,
	setDragOverId,
	serverPersonSecurities,
	localRanks,
	setLocalRanks,
	setUnrankedSecurities,
	selectedEntityId,
	upsertSecurityRank,
	deleteSecurityRank,
}: HandleSecurityDragEndProps) => {
	const { active, over } = event;
	const activeData = active.data.current as { draggedFromId?: DragAndDropId };

	setDragActiveId(null);
	setDragOverId(null);

	if (!over) {
		return;
	}

	const dragItemId = active.id as string;
	const dropZoneId = over.id as string;

	// Find the dragged security
	const draggedSecurity = serverPersonSecurities.find(
		(sec) => makeSecurityRankId(sec) === dragItemId
	);
	const draggedFromRankId = activeData?.draggedFromId;

	if (!draggedSecurity) {
		return;
	}

	// Build the drag group: main security + any warrant cards that reference it
	// Warrant cards are "shadow" cards that follow their main card in the UI.
	// They have warrantOverId pointing to the main security and share the same securityFeatureType.
	// Example: If dragging "Security A - RESIDUE", all warrant cards with
	// warrantOverId="Security A's ID" and securityFeatureType="RESIDUE" will move with it.
	const warrantCards = serverPersonSecurities.filter(
		(sec) =>
			sec.warrantOverId === draggedSecurity.securityId &&
			sec.securityFeatureType === draggedSecurity.securityFeatureType
	);
	const securitiesToMove = [draggedSecurity, ...warrantCards];

	switch (dropZoneId) {
		case DragAndDropId.sidebar: {
			// A RESIDUE card is pinned to the single residue tier and can never be unranked, so a
			// drop onto the sidebar is a no-op for it.
			if (draggedSecurity.securityFeatureType === SecurityFeatureType.RESIDUE) {
				break;
			}

			handleDropToSidebar({
				draggedFromRankId,
				securitiesToMove,
				setLocalRanks,
				setUnrankedSecurities,
			});

			if (draggedFromRankId !== DragAndDropId.sidebar) {
				deleteSecurityRank(draggedSecurity);
			}

			break;
		}
		case DragAndDropId.placeholderRank:
		case DragAndDropId.rankingContent: {
			handleDropToRankingContent({
				draggedFromRankId,
				securitiesToMove,
				localRanks,
				selectedEntityId,
				setLocalRanks,
				setUnrankedSecurities,
			});

			upsertSecurityRank({
				securityId: draggedSecurity.securityId,
				securityFeatureType: draggedSecurity.securityFeatureType,
				isMergedIncomeRight: !!draggedSecurity?.isMergedIncomeRight,
			});
			break;
		}
		default: {
			const targetRank = localRanks.find((rank) => rank.id === dropZoneId);

			// The residue tier accepts no drops: residue cards are never draggable, other
			// features may not rank into it, and the tier may be a synthesised one whose id
			// must never reach the API.
			if (targetRank && !targetRank.isResidue) {
				handleDropToRankContainer({
					draggedFromRankId,
					securitiesToMove,
					targetRank,
					setLocalRanks,
					setUnrankedSecurities,
				});

				upsertSecurityRank({
					securityId: draggedSecurity.securityId,
					securityFeatureType: draggedSecurity.securityFeatureType,
					rankId: targetRank.id,
					isMergedIncomeRight: !!draggedSecurity?.isMergedIncomeRight,
				});
			}
			break;
		}
	}
};

const handleDropToSidebar = (
	params: Pick<HandleSecurityDragEndProps, 'setLocalRanks' | 'setUnrankedSecurities'> & {
		draggedFromRankId: DragAndDropId | undefined;
		securitiesToMove: SecurityWithRank[];
	}
) => {
	const { draggedFromRankId, securitiesToMove, setLocalRanks, setUnrankedSecurities } = params;

	// Already unranked - no action needed
	if (draggedFromRankId === DragAndDropId.sidebar) {
		return;
	}

	const securityIds = new Set(securitiesToMove.map((sec) => makeSecurityRankId(sec)));

	// Optimistic UI update: Remove all securities in the group from the source rank
	setLocalRanks((prev) =>
		prev.map((rank) =>
			rank.id === draggedFromRankId
				? {
						...rank,
						securities: rank.securities.filter(
							(sec) => !securityIds.has(makeSecurityRankId(sec))
						),
					}
				: rank
		)
	);

	setUnrankedSecurities((prev) => [...prev, ...securitiesToMove]);
};

const handleDropToRankContainer = (
	params: Pick<HandleSecurityDragEndProps, 'setLocalRanks' | 'setUnrankedSecurities'> & {
		draggedFromRankId: DragAndDropId | undefined;
		securitiesToMove: SecurityWithRank[];
		targetRank: RankWithSecurities;
	}
) => {
	const {
		draggedFromRankId,
		securitiesToMove,
		targetRank,
		setLocalRanks,
		setUnrankedSecurities,
	} = params;

	// Already in target rank - no action needed
	if (draggedFromRankId === targetRank.id) {
		return;
	}

	const securityIds = new Set(securitiesToMove.map((sec) => makeSecurityRankId(sec)));

	// SCENARIO 1: Moving from unranked (sidebar) to a rank
	if (draggedFromRankId === DragAndDropId.sidebar) {
		// Remove from unranked list
		setUnrankedSecurities((prev) =>
			prev.filter((sec) => !securityIds.has(makeSecurityRankId(sec)))
		);
		// Add to target rank
		setLocalRanks((prev) =>
			prev.map((rank) =>
				rank.id === targetRank.id
					? {
							...rank,
							securities: [...rank.securities, ...securitiesToMove],
						}
					: rank
			)
		);
	} else {
		// SCENARIO 2: Moving from one rank to another rank
		setLocalRanks((prev) =>
			prev.map((rank) => {
				// Remove from source rank
				if (rank.id === draggedFromRankId) {
					return {
						...rank,
						securities: rank.securities.filter(
							(sec) => !securityIds.has(makeSecurityRankId(sec))
						),
					};
				}

				// Add to target rank
				if (rank.id === targetRank.id) {
					return {
						...rank,
						securities: [...rank.securities, ...securitiesToMove],
					};
				}

				return rank;
			})
		);
	}
};

const handleDropToRankingContent = (
	params: Pick<
		HandleSecurityDragEndProps,
		'selectedEntityId' | 'localRanks' | 'setLocalRanks' | 'setUnrankedSecurities'
	> & {
		draggedFromRankId: DragAndDropId | undefined;
		securitiesToMove: SecurityWithRank[];
	}
) => {
	const {
		draggedFromRankId,
		securitiesToMove,
		localRanks,
		selectedEntityId,
		setLocalRanks,
		setUnrankedSecurities,
	} = params;

	// Create a temporary rank with all securities (main + warrants)
	// This rank will be replaced by the server when the mutation succeeds
	const newRank = createTempRank({
		ranksLength: localRanks.length,
		selectedEntityId,
		securitiesToMove,
	});

	const securityIds = new Set(securitiesToMove.map((sec) => makeSecurityRankId(sec)));

	// Optimistic update: create new rank with all securities in the group
	if (draggedFromRankId === DragAndDropId.sidebar) {
		// Remove from unranked list
		setUnrankedSecurities((prev) =>
			prev.filter((sec) => !securityIds.has(makeSecurityRankId(sec)))
		);
		// Add new rank in the correct position based on feature type
		setLocalRanks((prev) => sortRanksOrder([...prev], newRank, securitiesToMove[0]));
	} else {
		// Remove from source rank and add new rank
		setLocalRanks((prev) => {
			const ranks = [
				...prev.map((rank) =>
					rank.id === draggedFromRankId
						? {
								...rank,
								securities: rank.securities.filter(
									(sec) => !securityIds.has(makeSecurityRankId(sec))
								),
							}
						: rank
				),
			];

			return sortRanksOrder(ranks, newRank, securitiesToMove[0]);
		});
	}
};

const createTempRank = (params: {
	ranksLength: number;
	selectedEntityId: string;
	securitiesToMove: SecurityWithRank[];
}): RankWithSecurities => {
	const { ranksLength, selectedEntityId, securitiesToMove } = params;

	return {
		id: `temp-${Date.now()}`,
		displayName: `Rank ${ranksLength + 1}`,
		sortOrder: ranksLength,
		displaySortOrder: ranksLength,
		securities: securitiesToMove,
		issuerId: selectedEntityId,
		isTracking: false,
		isResidue: false,
		deleted: false,
		updatedAt: '',
		createdAt: '',
		validUnrankedSecurities: [],
		dealId: '',
	};
};

const sortRanksOrder = (
	ranks: RankWithSecurities[],
	newRank: RankWithSecurities,
	draggedSecurity: SecurityWithRank
): RankWithSecurities[] => {
	const trackingRankExists = ranks.some((rank) => rank.isTracking);

	if (draggedSecurity.securityFeatureType === SecurityFeatureType.RESIDUE) {
		ranks.push(newRank);
		return ranks;
	} else if (trackingRankExists) {
		ranks.splice(ranks.findIndex((rank) => rank.isTracking) + 1, 0, newRank);
		return ranks;
	} else {
		ranks.unshift(newRank);
		return ranks;
	}
};
